/**
 * Demo/presenter scope simulation.
 *
 * This module is the shared source of truth for "role" scope sets used both
 * by Playwright e2e fixtures (`e2e/fixtures/auth.ts` re-exports from here)
 * and by the in-app, dev/demo-only profile switcher (`DemoProfileSwitcher`,
 * `Login`'s demo section). It mints unsigned `alg:none` JWTs the workbench
 * `AuthProvider` will client-side-decode — there is no signature, and the
 * backend remains the authority on every real request. Never used outside
 * dev/demo builds (see `isDemoAuthEnabled`).
 */

/** Pre-baked roles understood by the workbench LeftNav scope-gating. */
export const ROLE_SCOPES: Record<string, readonly string[]> = {
  trader: [
    'deals:read',
    'deals:capture',
    'deals:amend',
    'deals:pre-check',
    'pretrade:read',
    'instruments:create',
    'parties:internal:read',
    'positions:read',
    'valuations:write',
    'valuations:read',
    'simulations:write',
    'simulations:read',
    'credit:read',
    'risk:read',
    'cashflows:fix',
    'instruments:read',
    'indexes:read',
    'approvals:read',
    'approvals:approve',
    'approvals:reject',
    'approvals:escalate',
  ],
  middle_office: [
    'deals:read',
    'positions:read',
    'settlements:read',
    'settlements:instruct',
    'payments:read',
    'payments:generate',
    'payments:send',
    'payments:acknowledge',
    'payments:cancel',
    'netting:read',
    'netting:execute',
    'parties:internal:read',
    'bank_accounts:read',
    'bank_accounts:write',
    'instructions:read',
    'instructions:create',
    'instructions:claim',
    'instructions:execute',
    'workbench:templates:read',
    'workbench:templates:write',
    'user:preferences:read',
    'user:preferences:write',
    'curves:read',
    'fixings:read',
    'fixings:capture',
    'indexes:read',
    'indexes:manage',
    'market-data:read',
    'market-data:publish',
  ],
  back_office: [
    'journals:read',
    'journals:post',
    'journals:period_close',
    'journals:reverse',
    'cash:read',
    'reports:read',
    'closeout:read',
    'closeout:execute',
  ],
  admin: [
    'audit:read',
    'audit:verify',
    'config:read',
    'config:admin',
    'config:manage',
    'system:read',
    'system:batch',
    'system:admin',
    'parties:internal:read',
    'parties:internal:write',
    'plugins:admin',
    'artifacts:read',
    'artifacts:upload',
    'artifacts:submit',
    'artifacts:review',
    'artifacts:activate',
    'calendars:read',
    'calendars:manage',
    'stp:read',
    'stp:manage',
    'bank_accounts:read',
    'bank_accounts:write',
    'users:admin',
    'entitlements:admin',
  ],
  'amend-user': ['instruments:read', 'instruments:update'],
  'supersede-user': ['instruments:read', 'instruments:update', 'instruments:supersede'],
  /** Convenience role with everything the e2e tests might need. */
  full: [],
};

// Compute "full" as the union of the original legacy roles only (trader,
// middle_office, back_office, admin, amend-user, supersede-user). This is
// the exact set existing e2e specs and DEMO_PROFILES.full_access rely on —
// it intentionally does NOT fold in the newer demo-only role sets
// (portfolio_manager, accounting) defined below in DEMO_PROFILES, since
// those are UI-presenter conveniences, not e2e-fixture roles.
ROLE_SCOPES.full = Array.from(
  new Set(
    Object.entries(ROLE_SCOPES)
      .filter(([k]) => k !== 'full')
      .flatMap(([, v]) => v),
  ),
);

/** Browser-safe UTF-8 → base64url encoder (no Node `Buffer` dependency). */
function b64url(input: string): string {
  const bytes = new TextEncoder().encode(input);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
}

/**
 * Mint a synthetic unsigned JWT the workbench AuthProvider will accept.
 * The provider only base64-decodes the payload — it does not verify the
 * signature — which is appropriate here because this is dev/demo-only
 * scope simulation (or, in Playwright, a stubbed backend).
 */
export function mintTestToken(opts: {
  sub?: string;
  scopes?: readonly string[];
  role?: keyof typeof ROLE_SCOPES;
}): string {
  const header = { alg: 'none', typ: 'JWT' };
  const scopes = opts.scopes ?? (opts.role ? ROLE_SCOPES[opts.role] : ROLE_SCOPES.full) ?? [];
  const payload = {
    sub: opts.sub ?? `e2e-${opts.role ?? 'full'}`,
    scopes: Array.from(scopes),
    iat: Math.floor(Date.now() / 1000),
    exp: Math.floor(Date.now() / 1000) + 60 * 60,
  };
  return `${b64url(JSON.stringify(header))}.${b64url(JSON.stringify(payload))}.sig`;
}

export type DemoProfile = {
  id: string;
  labelKey: string;
  descriptionKey: string;
  scopes: readonly string[];
};

/**
 * Profiles surfaced in the dev/demo-only profile switcher (Login screen +
 * TopBar `DemoProfileSwitcher`). Scope sets for the two NEW profiles
 * (`portfolio_manager`, `accounting`) are drawn only from scope strings
 * confirmed present in `src/app/navConfig.ts`'s `scopes` arrays, except
 * `accounting` deliberately includes `journals:reverse`, a real
 * pre-existing scope string used by `ROLE_SCOPES.back_office` even though
 * no current navConfig item gates on it alone.
 */
export const DEMO_PROFILES: DemoProfile[] = [
  {
    id: 'administrator',
    labelKey: 'auth:demoProfiles.administrator.label',
    descriptionKey: 'auth:demoProfiles.administrator.description',
    scopes: ROLE_SCOPES.admin ?? [],
  },
  {
    id: 'front_office',
    labelKey: 'auth:demoProfiles.front_office.label',
    descriptionKey: 'auth:demoProfiles.front_office.description',
    scopes: ROLE_SCOPES.trader ?? [],
  },
  {
    id: 'middle_office',
    labelKey: 'auth:demoProfiles.middle_office.label',
    descriptionKey: 'auth:demoProfiles.middle_office.description',
    scopes: ROLE_SCOPES.middle_office ?? [],
  },
  {
    id: 'back_office',
    labelKey: 'auth:demoProfiles.back_office.label',
    descriptionKey: 'auth:demoProfiles.back_office.description',
    scopes: ROLE_SCOPES.back_office ?? [],
  },
  {
    id: 'portfolio_manager',
    labelKey: 'auth:demoProfiles.portfolio_manager.label',
    descriptionKey: 'auth:demoProfiles.portfolio_manager.description',
    scopes: [
      'positions:read',
      'risk:read',
      'valuations:read',
      'simulations:read',
      'simulations:write',
      'credit:read',
      'deals:read',
      'indexes:read',
      'fixings:read',
      'reports:read',
    ],
  },
  {
    id: 'accounting',
    labelKey: 'auth:demoProfiles.accounting.label',
    descriptionKey: 'auth:demoProfiles.accounting.description',
    scopes: [
      'journals:read',
      'journals:post',
      'journals:period_close',
      'journals:reverse',
      'cash:read',
      'closeout:read',
      'closeout:execute',
      'reports:read',
    ],
  },
  {
    id: 'full_access',
    labelKey: 'auth:demoProfiles.full_access.label',
    descriptionKey: 'auth:demoProfiles.full_access.description',
    scopes: ROLE_SCOPES.full ?? [],
  },
];

/**
 * Dev/demo-only gate. The demo profile switcher (Login section + TopBar
 * dropdown) must never appear in a normal production build — only when
 * running the Vite dev server, or when an operator explicitly opts in via
 * `VITE_DEMO_AUTH=true` (e.g. a demo-flavored preview/staging build).
 */
export function isDemoAuthEnabled(): boolean {
  return import.meta.env.DEV === true || import.meta.env.VITE_DEMO_AUTH === 'true';
}
